import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FormFieldLabel from './FormFieldLabel';
import { BRAND_ORANGE, PLACEHOLDER_COLOR } from './manageEventFieldStyles';

const UploadZone = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'hasImage',
      'stretch',
      'fill',
      'hasError',
      'squareAspect',
      'squareSize',
      'compact',
      'disabled',
    ].includes(prop),
})(({ hasImage, stretch, fill, hasError, squareAspect, squareSize, compact, disabled }) => ({
  border: `${compact ? 1 : 2}px dashed ${hasError ? '#d32f2f' : BRAND_ORANGE}`,
  borderRadius: compact ? 6 : 10,
  width: '100%',
  ...(squareAspect
    ? {
        aspectRatio: '1 / 1',
        width: squareSize ?? (stretch ? '100%' : 280),
        maxWidth: '100%',
        flex: stretch ? 1 : 'none',
        alignSelf: stretch ? 'stretch' : 'flex-start',
      }
    : {
        minHeight: (() => {
          if (hasImage) return stretch ? 200 : 140;
          if (stretch || fill) return 0;
          return 160;
        })(),
      }),
  flex: stretch || fill ? 1 : 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'background.paper',
  opacity: disabled ? 0.55 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  transition: 'background-color 0.2s, border-color 0.2s, opacity 0.2s',
  '&:hover': {
    backgroundColor: disabled ? 'background.paper' : 'action.hover',
  },
}));

const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  inset: 0,
});

const ImageUpload = ({
  label,
  preview,
  onImageChange,
  onImageSelected,
  stretch = false,
  fill = false,
  required = false,
  error = false,
  helperText,
  dimensionHint,
  formatHint,
  squareAspect = false,
  squareSize = null,
  compact = false,
  accept = 'image/*',
  uploadLabel = 'Upload an image',
  selectedFileName = null,
  disabled = false,
  validateImage,
  validateFile,
}) => {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState('');

  const displayError = helperText || localError;
  const hasError = error || Boolean(localError);
  const hasSelectedFile = Boolean(preview) || Boolean(selectedFileName);
  const validator = validateFile || validateImage;

  const processFile = async (file) => {
    if (!file || disabled) return;

    if (validator) {
      const result = await Promise.resolve(validator(file));
      if (!result.valid) {
        setLocalError(result.message || 'Invalid file');
        return;
      }
    } else if (!file.type?.startsWith('image/')) {
      return;
    }

    setLocalError('');

    if (onImageSelected) {
      await onImageSelected(file);
      return;
    }

    if (onImageChange) {
      onImageChange(file, URL.createObjectURL(file));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: squareAspect && !stretch ? 'flex-start' : 'stretch',
        width: '100%',
        ...(stretch &&
          !squareAspect && {
            height: '100%',
            minHeight: 280,
          }),
        ...(fill && {
          height: '100%',
          minHeight: 160,
        }),
      }}
    >
      {compact ? (
        <Box sx={{ mb: 0.25 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem', lineHeight: 1.3 }}
          >
            {label}
            {required ? (
              <Typography component="span" color="error" sx={{ ml: 0.25 }}>
                *
              </Typography>
            ) : null}
          </Typography>
          {dimensionHint ? (
            <Typography
              variant="caption"
              sx={{
                color: PLACEHOLDER_COLOR,
                fontWeight: 400,
                fontSize: '0.6875rem',
                lineHeight: 1.2,
                display: 'block',
                mt: 0.125,
              }}
            >
              {dimensionHint}
            </Typography>
          ) : null}
          {formatHint ? (
            <Typography
              variant="caption"
              sx={{
                color: PLACEHOLDER_COLOR,
                fontWeight: 400,
                fontSize: '0.6875rem',
                lineHeight: 1.2,
                display: 'block',
                mt: dimensionHint ? 0 : 0.125,
              }}
            >
              {formatHint}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <FormFieldLabel required={required} sx={{ mb: 0.5 }}>
          {label}
        </FormFieldLabel>
      )}
      {dimensionHint && !compact ? (
        <Typography
          variant="caption"
          sx={{ color: PLACEHOLDER_COLOR, mb: formatHint ? 0.25 : 1, display: 'block' }}
        >
          Minimum size: {dimensionHint}
        </Typography>
      ) : null}
      {formatHint && !compact ? (
        <Typography variant="caption" sx={{ color: PLACEHOLDER_COLOR, mb: 1, display: 'block' }}>
          {formatHint}
        </Typography>
      ) : null}
      <UploadZone
        hasImage={hasSelectedFile}
        hasError={hasError}
        stretch={stretch}
        fill={fill}
        compact={compact}
        disabled={disabled}
        squareAspect={squareAspect}
        squareSize={squareSize}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void processFile(e.dataTransfer.files?.[0]);
        }}
      >
        {preview ? (
          <PreviewImage src={preview} alt={`${label} preview`} />
        ) : (
          <>
            <FileUploadOutlinedIcon
              sx={{ fontSize: compact ? 22 : 36, color: BRAND_ORANGE, mb: compact ? 0 : 1 }}
            />
            {!compact ? (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: selectedFileName ? 'text.primary' : PLACEHOLDER_COLOR,
                    fontWeight: selectedFileName ? 500 : 400,
                    px: 2,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedFileName || uploadLabel}
                </Typography>
                {selectedFileName ? (
                  <Typography
                    variant="caption"
                    sx={{ color: PLACEHOLDER_COLOR, mt: 0.5, px: 2, textAlign: 'center' }}
                  >
                    Click or drag to replace
                  </Typography>
                ) : null}
              </>
            ) : null}
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          disabled={disabled}
          onChange={(e) => {
            void processFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </UploadZone>
      {displayError && (
        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.75, display: 'block' }}>
          {displayError}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload;
